/**
 * Sentry error monitoring initialization.
 *
 * Must be imported BEFORE React renders (in main.tsx).
 * Reads config from VITE_SENTRY_DSN / VITE_SENTRY_ENVIRONMENT env vars.
 */
import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const environment = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || "development";

export const isSentryEnabled = Boolean(dsn);

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    // Performance monitoring — sample 20% of transactions
    tracesSampleRate: 0.2,
    // Session replay for debugging UI issues in production
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Don't send PII
    sendDefaultPii: false,
    // Filter out noisy errors from browser extensions
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const msg =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message + (error.stack ?? "")
            : "";

      // Suppress browser extension noise
      if (
        msg.includes("detectStore") ||
        msg.includes("moz-extension://") ||
        msg.includes("chrome-extension://") ||
        msg.includes("h1-check.js")
      ) {
        return null;
      }
      return event;
    },
    // Filter health-check / static-asset transactions
    beforeSendTransaction(event) {
      const name = event.transaction ?? "";
      if (name.includes("/health") || name.includes("/docs")) {
        return null;
      }
      return event;
    },
  });

  if (import.meta.env.DEV) {
    console.info("[Sentry] Initialized —", environment);
  }
} else if (import.meta.env.DEV) {
  console.info("[Sentry] Disabled — no VITE_SENTRY_DSN configured");
}

/**
 * Set the authenticated user on Sentry scope.
 * Call after login; call with null on logout.
 */
export function setSentryUser(userId: string | null) {
  if (!isSentryEnabled) return;
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
  }
}

// Re-export Sentry for use in ErrorBoundary
export { Sentry };
